// Updated AdminDashboard.js - Complete implementation with monitoring
import React, { useState, useEffect } from "react";
import {
  Users,
  CreditCard,
  User as UserIcon,
  Trash2,
  Edit,
  X,
  Check,
  AlertCircle,
  Activity,
  Cpu,
} from "lucide-react";
import api from "./api";
import CreditConfigManager from "./CreditConfigManager";
import ProcessorManager from "./ProcessorManager";
import ProcessorMonitoringDashboard from "./ProcessorMonitoringDashboard";
import "./AdminDashboard.css";

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    email: "",
    credits: 0,
    isAdmin: false,
  });

  useEffect(() => {
    if (activeTab === "users") {
      fetchUsers();
    }
  }, [activeTab]);

  const fetchUsers = async () => {
    try {
      const response = await api.get("/admin/users");
      setUsers(response.data);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch users");
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      email: user.email,
      credits: user.credits,
      isAdmin: user.isAdmin,
    });
  };

  const handleUpdateUser = async () => {
    try {
      await api.patch(`/admin/users/${editingUser._id}`, editForm);
      setEditingUser(null);
      await fetchUsers();
    } catch (err) {
      setError("Failed to update user");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) {
      return;
    }

    try {
      await api.delete(`/admin/users/${userId}`);
      await fetchUsers();
    } catch (err) {
      setError("Failed to delete user");
    }
  };

  if (loading && activeTab === "users") {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1 className="admin-title">
          <Users size={24} />
          Admin Dashboard
        </h1>

        <div className="admin-tabs">
          <button
            className={`tab-btn ${activeTab === "users" ? "active" : ""}`}
            onClick={() => setActiveTab("users")}
          >
            <UserIcon size={18} />
            Users
          </button>
          <button
            className={`tab-btn ${activeTab === "credits" ? "active" : ""}`}
            onClick={() => setActiveTab("credits")}
          >
            <CreditCard size={18} />
            Credit Config
          </button>
          <button
            className={`tab-btn ${activeTab === "processors" ? "active" : ""}`}
            onClick={() => setActiveTab("processors")}
          >
            <Cpu size={18} />
            Processors
          </button>
          <button
            className={`tab-btn ${activeTab === "monitoring" ? "active" : ""}`}
            onClick={() => setActiveTab("monitoring")}
          >
            <Activity size={18} />
            Monitoring
          </button>
        </div>
      </div>

      {error && (
        <div className="error-alert">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {activeTab === "users" && (
        <div className="users-table-container">
          <div className="users-table-header">
            <h2>User Management</h2>
            <div className="users-stats">
              <div className="stat-item">
                <span className="stat-label">Total Users</span>
                <span className="stat-value">{users.length}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Admin Users</span>
                <span className="stat-value">
                  {users.filter((user) => user.isAdmin).length}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Total Credits</span>
                <span className="stat-value">
                  {users.reduce((sum, user) => sum + (user.credits || 0), 0)}
                </span>
              </div>
            </div>
          </div>

          <table className="users-table">
            <thead className="table-header">
              <tr>
                <th>User</th>
                <th>Credits</th>
                <th>Role</th>
                <th>Repositories</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {users.map((user) => (
                <tr key={user._id}>
                  {editingUser?._id === user._id ? (
                    <>
                      <td className="table-cell">
                        <input
                          type="email"
                          value={editForm.email}
                          onChange={(e) =>
                            setEditForm((f) => ({
                              ...f,
                              email: e.target.value,
                            }))
                          }
                          className="edit-input edit-input-full"
                        />
                      </td>
                      <td className="table-cell">
                        <input
                          type="number"
                          value={editForm.credits}
                          onChange={(e) =>
                            setEditForm((f) => ({
                              ...f,
                              credits: parseInt(e.target.value) || 0,
                            }))
                          }
                          className="edit-input edit-input-small"
                        />
                      </td>
                      <td className="table-cell">
                        <select
                          value={editForm.isAdmin}
                          onChange={(e) =>
                            setEditForm((f) => ({
                              ...f,
                              isAdmin: e.target.value === "true",
                            }))
                          }
                          className="edit-select"
                        >
                          <option value="false">User</option>
                          <option value="true">Admin</option>
                        </select>
                      </td>
                      <td className="table-cell">
                        <span className="repo-count">
                          {user.repos?.length || 0} repos
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="action-buttons">
                          <button
                            onClick={handleUpdateUser}
                            className="btn-save"
                          >
                            <Check size={20} />
                          </button>
                          <button
                            onClick={() => setEditingUser(null)}
                            className="btn-cancel"
                          >
                            <X size={20} />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="table-cell">
                        <div className="user-info">
                          <UserIcon size={20} />
                          <div>
                            <div className="user-email">{user.email}</div>
                            {user.dealCode && (
                              <small className="deal-code">
                                Deal: {user.dealCode}
                              </small>
                            )}
                            <div className="user-meta">
                              <span className="joined-date">
                                Joined{" "}
                                {new Date(user.createdAt).toLocaleDateString()}
                              </span>
                              {user.paymentMethodAdded && (
                                <span className="payment-badge">
                                  Payment Added
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="credit-info">
                          <CreditCard size={20} />
                          <div>
                            <div className="credit-amount">
                              {user.credits || 0}
                            </div>
                            {user.creditHistory &&
                              user.creditHistory.length > 0 && (
                                <small className="credit-history">
                                  {user.creditHistory.length} transactions
                                </small>
                              )}
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <span
                          className={`badge ${
                            user.isAdmin ? "badge-admin" : "badge-user"
                          }`}
                        >
                          {user.isAdmin ? "Admin" : "User"}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="repo-info">
                          <span className="repo-count">
                            {user.repos?.length || 0} repos
                          </span>
                          {user.repos && user.repos.length > 0 && (
                            <div className="repo-status">
                              {
                                user.repos.filter(
                                  (repo) => repo.status === "completed"
                                ).length
                              }{" "}
                              completed
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="action-buttons">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="btn-edit"
                            title="Edit User"
                          >
                            <Edit size={20} />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user._id)}
                            className="btn-delete"
                            title="Delete User"
                            disabled={
                              user.isAdmin &&
                              users.filter((u) => u.isAdmin).length === 1
                            }
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {users.length === 0 && (
            <div className="no-users">
              <p>No users found.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === "credits" && <CreditConfigManager />}

      {activeTab === "processors" && <ProcessorManager />}

      {activeTab === "monitoring" && <ProcessorMonitoringDashboard />}
    </div>
  );
};

export default AdminDashboard;
